import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { ShieldCheck, Mail, Lock, ArrowRight, KeyRound, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Login = () => {
  const { loginStep1, verifyOtp, resendOtp, isAuthenticated, isLoading, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [step, setStep] = useState('credentials'); // 'credentials' | 'otp'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [portalHelpLoading, setPortalHelpLoading] = useState(false);
  const [portalHelpSent, setPortalHelpSent] = useState(false);

  const handlePortalHelp = async () => {
    const target = email.trim().toLowerCase();
    if (!target || !target.includes('@')) {
      toast.error('Please type your email in the field above first.');
      return;
    }
    setPortalHelpLoading(true);
    try {
      await axios.post(`${API}/portal-invites/help`, { email: target });
      setPortalHelpSent(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setPortalHelpLoading(false);
    }
  };

  const from = location.state?.from?.pathname || '/';

  const destinationFor = (u) => (u?.role === 'customer' ? '/portal' : from);

  useEffect(() => {
    if (isAuthenticated && user) navigate(destinationFor(user), { replace: true });
  }, [isAuthenticated, user, navigate, from]);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  const handleCredentials = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    const result = await loginStep1(email.trim().toLowerCase(), password);
    setSubmitting(false);
    if (result.ok) {
      // Trusted-device fast path — server skipped OTP, user already set
      if (result.step === 'authenticated' && result.user) {
        toast.success(`Welcome back, ${result.user.name}`);
        navigate(destinationFor(result.user), { replace: true });
        return;
      }
      setStep('otp');
      setResendCooldown(30);
      toast.success('Verification code sent to your email');
    } else {
      setError(result.error);
    }
  };

  const handleOtp = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    const result = await verifyOtp(email.trim().toLowerCase(), code.trim());
    setSubmitting(false);
    if (result.ok) {
      toast.success(`Welcome, ${result.user.name}`);
      navigate(destinationFor(result.user), { replace: true });
    } else {
      setError(result.error);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;
    const result = await resendOtp(email.trim().toLowerCase());
    if (result.ok) {
      setResendCooldown(30);
      toast.success('A new code has been sent');
    } else {
      toast.error(result.error);
    }
  };

  if (isLoading) return null;

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Brand panel */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#0D5B69] text-white p-12 flex-col justify-between relative overflow-hidden">
        <div className="absolute inset-0 opacity-10 pointer-events-none">
          <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-[#80C030] blur-3xl"></div>
          <div className="absolute -bottom-24 -left-24 w-96 h-96 rounded-full bg-[#80C030] blur-3xl"></div>
        </div>

        <div className="relative z-10">
          <div className="mb-12">
            <div className="inline-block bg-white rounded-lg px-4 py-3 shadow-lg" data-testid="login-brand-logo">
              <img src="/csg-logo.png" alt="CSG Security" className="h-8 w-auto" />
            </div>
            <p className="text-xs text-cyan-100/70 mt-3 uppercase tracking-[0.2em] font-semibold">
              Operations Management
            </p>
          </div>

          <h2 className="text-4xl font-heading font-bold leading-tight mb-4">
            Digital Occurrence Book.
            <br />
            <span className="text-[#80C030]">Built for control rooms.</span>
          </h2>
          <p className="text-cyan-100/80 text-sm leading-relaxed max-w-md">
            Real-time incident logging, contractor dispatch, integrated mapping, and automated
            billing — all in one secure platform.
          </p>
        </div>

        <div className="relative z-10 grid grid-cols-3 gap-4 text-xs text-cyan-100/70">
          <div>
            <p className="text-2xl font-heading font-bold text-white">11</p>
            <p>Service types</p>
          </div>
          <div>
            <p className="text-2xl font-heading font-bold text-white">24/7</p>
            <p>Contractor uptime</p>
          </div>
          <div>
            <p className="text-2xl font-heading font-bold text-white">POPIA</p>
            <p>Audit-ready</p>
          </div>
        </div>
      </div>

      {/* Form panel */}
      <div className="flex-1 flex items-center justify-center p-6 bg-slate-50">
        <Card className="w-full max-w-md border-slate-200 shadow-sm">
          <CardContent className="p-8 space-y-6">
            <div className="lg:hidden flex justify-center mb-2">
              <img src="/csg-logo.png" alt="CSG Security" className="h-10 w-auto" />
            </div>

            {step === 'credentials' && (
              <>
                <div>
                  <h2 className="text-2xl font-heading font-bold text-slate-900">Sign in</h2>
                  <p className="text-sm text-slate-500 mt-1">
                    Enter your CSG email and password.
                  </p>
                </div>

                {error && (
                  <Alert variant="destructive" data-testid="login-error">
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                <form onSubmit={handleCredentials} className="space-y-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="email" className="text-xs uppercase tracking-wide text-slate-600">
                      Email address
                    </Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="email"
                        type="email"
                        required
                        autoComplete="email"
                        autoFocus
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="markus@csgconnect.co.za"
                        className="pl-9"
                        data-testid="email-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="password" className="text-xs uppercase tracking-wide text-slate-600">
                        Password
                      </Label>
                      <button
                        type="button"
                        className="text-xs text-[#0D5B69] hover:underline font-semibold"
                        onClick={() => navigate('/forgot-password')}
                        data-testid="forgot-password-link"
                      >
                        Forgot password?
                      </button>
                    </div>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="password"
                        type="password"
                        required
                        autoComplete="current-password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="••••••••"
                        className="pl-9"
                        data-testid="password-input"
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-[#80C030] hover:bg-[#6FA528] text-white gap-2 font-semibold"
                    disabled={submitting}
                    data-testid="sign-in-btn"
                  >
                    {submitting ? 'Signing in...' : 'Continue'}
                    {!submitting && <ArrowRight className="w-4 h-4" />}
                  </Button>
                </form>

                <p className="text-[11px] text-slate-400 text-center leading-relaxed">
                  Forgot your password? Use the link above and we'll email you a reset link.
                </p>

                <div className="text-center pt-2 border-t border-slate-100">
                  <button
                    type="button"
                    onClick={handlePortalHelp}
                    disabled={portalHelpLoading}
                    className="text-xs text-slate-500 hover:text-slate-800 underline underline-offset-2"
                    data-testid="portal-help-link"
                  >
                    {portalHelpLoading ? 'Sending…'
                      : "Customer portal: didn't receive an invitation, or trouble signing in?"}
                  </button>
                  {portalHelpSent && (
                    <p className="text-[11px] text-emerald-600 mt-1.5" data-testid="portal-help-sent">
                      ✓ If your email is on file, we&apos;ve just sent you a link.
                    </p>
                  )}
                </div>
              </>
            )}

            {step === 'otp' && (
              <>
                <button
                  type="button"
                  onClick={() => {
                    setStep('credentials');
                    setCode('');
                    setError('');
                  }}
                  className="text-xs text-slate-500 hover:text-slate-900"
                  data-testid="back-to-credentials-btn"
                >
                  ← Use a different email
                </button>

                <div>
                  <div className="w-12 h-12 bg-[#80C030]/15 rounded-lg flex items-center justify-center mb-4">
                    <KeyRound className="w-6 h-6 text-[#0D5B69]" />
                  </div>
                  <h2 className="text-2xl font-heading font-bold text-slate-900">
                    Check your email
                  </h2>
                  <p className="text-sm text-slate-500 mt-1">
                    We sent a 6-digit verification code to{' '}
                    <span className="font-medium text-slate-700">{email}</span>. It expires in 10
                    minutes.
                  </p>
                </div>

                {error && (
                  <Alert variant="destructive" data-testid="otp-error">
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                <form onSubmit={handleOtp} className="space-y-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="code" className="text-xs uppercase tracking-wide text-slate-600">
                      Verification code
                    </Label>
                    <Input
                      id="code"
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      pattern="[0-9]{6}"
                      maxLength={6}
                      autoFocus
                      required
                      value={code}
                      onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                      placeholder="000000"
                      className="text-center text-2xl tracking-[0.5em] font-mono h-14"
                      data-testid="otp-input"
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-[#80C030] hover:bg-[#6FA528] text-white gap-2 font-semibold"
                    disabled={submitting || code.length !== 6}
                    data-testid="verify-otp-btn"
                  >
                    {submitting ? 'Verifying...' : 'Verify & sign in'}
                    {!submitting && <ArrowRight className="w-4 h-4" />}
                  </Button>
                </form>

                <div className="flex items-center justify-center gap-1 text-xs text-slate-500">
                  Didn't receive the code?
                  <button
                    type="button"
                    onClick={handleResend}
                    disabled={resendCooldown > 0}
                    className="ml-1 text-[#0D5B69] hover:underline disabled:text-slate-300 disabled:no-underline gap-1 inline-flex items-center font-semibold"
                    data-testid="resend-otp-btn"
                  >
                    <RefreshCw className="w-3 h-3" />
                    {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend'}
                  </button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
